import React from 'react';

import { Challenge } from '@wix/ambassador-challenges-v1-challenge/types';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useSettings } from '@wix/tpa-settings/react';
import { dataHooks } from '@wix/data-hooks';

import {
  FCWithChildren,
  serverTimelineTypeToClientTimelineString,
} from '@wix/challenges-web-library';

import { ChallengeOverviewSection } from '../ChallengeOverviewSection';
import { ChallengeDetails } from '../../../../../components-shared/ChallengeDetails';
import { SocialGroup } from '../../../../../components-shared/SocialGroup';
import { MediaCover } from '../../../../../components-shared/MediaCover';
import {
  RewardInfo,
  RewardInfoCompleted,
} from '../../../../../components-shared/RewardInfo';
import { ChallengeStats } from '../ChallengeStats';

import { classes, st } from './ChallengeOverview.st.css';
import utils from '../../views/utils';
import settingsParams from '../../../settingsParams';
import { SidebarLayoutButton } from '../Buttons/SidebarLayoutButton/SidebarLayoutButton';
import { Fullscreen } from '../Fullscreen/Fullscreen';
import { SettingsKeysAbbreviation } from '../../../Settings/constants/settingsKeysAbbreviation';
import { useUser } from '../../../../../contexts/User/UserContext';
import { isUserCompleted } from '../../../../../contexts/User/helpers/userTypeHandlers';
import { covertTextToBadgesAlignment } from '../../../../../services/settingsHelpers';
import {
  useCSSPBEnabled,
  useCSSPBStyle,
} from '../../../../../hooks/useCSSPBStyle';
import { VisitorPageInstructors } from '../../../../ChallengesPage/Widget/views/VisitorPage/components/sections/VisitorPageInstructors';
import { getProgramAltImageText } from '../../../../../selectors/getProgramImageAltText';
import { useChallengeData } from '../../../../../contexts/storage-contexts/Challenge';

export interface IChallengeOverviewProps {
  className?: string;
  challenge: Challenge;
  prefix?: React.ReactElement;
  goToCurrentStep?(): void;
  bottomControls?: React.ReactNode;
  pageRef?: React.RefObject<HTMLElement>;
}

export const challengeOverviewDataHooks = dataHooks<{
  main;
  media;
  author;
}>('challenge-overview');

const Duration: FCWithChildren<Partial<IChallengeOverviewProps>> = ({
  challenge,
}) => {
  const { t } = useTranslation();
  const settings = useSettings();
  const { language } = useEnvironment();

  const { showHeaderDate } = utils.getBaseViewOptionsForSidebar(
    challenge,
    settings,
  );

  const durationString = serverTimelineTypeToClientTimelineString(
    challenge?.settings?.timelineType as any,
    language,
    t,
    'challenge-card.duration-string.ongoing',
    `challenge-card.duration-string.flexible.days_icu`,
    `challenge-card.duration-string.flexible.weeks_icu`,
    'challenge-card.duration-string.no-limit',
  );

  return showHeaderDate ? (
    <p className={classes.duration}>{durationString}</p>
  ) : null;
};

const OverviewMedia: FCWithChildren<Partial<IChallengeOverviewProps>> = ({
  challenge,
}) => {
  const isShouldBeRendered = challenge?.settings?.description?.media;

  return isShouldBeRendered ? (
    <MediaCover
      fit="contain"
      altText={getProgramAltImageText(challenge)}
      maxHeight={500}
      media={challenge.settings.description.media}
      dataHook={challengeOverviewDataHooks.media()}
    />
  ) : null;
};

const Details: FCWithChildren<Partial<IChallengeOverviewProps>> = ({
  challenge,
}) => {
  const { isMobile } = useEnvironment();

  return (
    <ChallengeDetails
      isMobile={isMobile}
      className={classes.challengeDetails}
      details={challenge?.settings?.description?.details}
    />
  );
};

const Group: FCWithChildren<
  Partial<IChallengeOverviewProps> & {
    Button?: FCWithChildren;
  } & React.HtmlHTMLAttributes<HTMLElement>
> = ({ Button, style }) => {
  const { t } = useTranslation();

  return (
    <SocialGroup
      Button={Button}
      className={classes.socialGroup}
      title={t('live-site.groups.section-title')}
      subTitle={t('live-site.groups.section-subtitle.joined')}
      showButton={true}
      style={style}
    />
  );
};

const Reward: FCWithChildren<Partial<IChallengeOverviewProps>> = ({
  challenge,
}) => {
  const { participant } = useUser();
  const settings = useSettings();
  const alignment = covertTextToBadgesAlignment(
    utils.getContentAlignByType(settings),
  );

  if (
    isUserCompleted(participant?.transitions[0]?.state) ||
    participant?.certificate
  ) {
    return (
      <RewardInfoCompleted
        className={classes.rewardInfo}
        rewards={challenge.settings.rewards}
      />
    );
  }

  return (
    <RewardInfo
      className={st(classes.rewardInfo, {
        alignment,
      })}
      rewards={challenge.settings.rewards}
    />
  );
};

const Stats: FCWithChildren<Partial<IChallengeOverviewProps>> = ({
  challenge,
}) => {
  return (
    <ChallengeStats className={classes.challengeStats} challenge={challenge} />
  );
};

const Instructors: FCWithChildren<Partial<IChallengeOverviewProps>> = () => {
  const settings = useSettings();

  return (
    <VisitorPageInstructors alignment={utils.getContentAlignByType(settings)} />
  );
};

export const ChallengeOverview: FCWithChildren<IChallengeOverviewProps> = ({
  className,
  prefix,
  challenge,
  pageRef,
}: IChallengeOverviewProps) => {
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const settings = useSettings();
  const alignment = utils.getContentAlignByType(settings);

  const {
    challengeData: { instructorsData },
  } = useChallengeData();
  const cssPBEnabled = useCSSPBEnabled();
  const isAuthorSectionVisible =
    (cssPBEnabled || settings.get(settingsParams.displayOwner)) &&
    instructorsData?.instructors?.length;
  const getCSSPBStyle = useCSSPBStyle();
  const authorSectionTitle = settings.get(
    settingsParams[SettingsKeysAbbreviation.visitorPageInstructorTitle],
  );

  const showRewards =
    cssPBEnabled || settings.get(settingsParams.displayRewards);
  const displayRewards = !!challenge?.settings?.rewards?.length && showRewards;

  const showGroup = cssPBEnabled || settings.get(settingsParams.displayGroup);
  return (
    <div
      className={`${st(classes.root, {
        mobile: isMobile,
        alignment,
      })} ${className}`}
      data-hook={challengeOverviewDataHooks.main()}
    >
      {prefix || null}

      <>
        <Fullscreen pageRef={pageRef} />
        <Duration challenge={challenge} />
        <Stats challenge={challenge} />
        <OverviewMedia challenge={challenge} />
        {!!challenge?.settings?.description?.details && (
          <ChallengeOverviewSection
            title={t('live.challenges-page-sidebar.about-title')}
          >
            <Details challenge={challenge} />
          </ChallengeOverviewSection>
        )}

        {displayRewards ? (
          <ChallengeOverviewSection
            title={t('challenge.overview.reward-title')}
            style={getCSSPBStyle({ displayVar: '--opgDisplayRewards' })}
          >
            <Reward challenge={challenge} />
          </ChallengeOverviewSection>
        ) : null}

        {showGroup ? (
          <Group
            Button={SidebarLayoutButton}
            style={getCSSPBStyle({ displayVar: '--opgDisplayGroup' })}
          />
        ) : null}

        {isAuthorSectionVisible ? (
          <ChallengeOverviewSection
            title={authorSectionTitle as string}
            dataHook={challengeOverviewDataHooks.author()}
            style={getCSSPBStyle({ displayVar: '--opgDisplayOwner' })}
          >
            <Instructors />
          </ChallengeOverviewSection>
        ) : null}
      </>
    </div>
  );
};
ChallengeOverview.displayName = 'ChallengeOverview';
ChallengeOverview.defaultProps = {
  className: '',
};
